import { ReactNode, useEffect, useRef, useState } from "react";

/**
 * Renders children only when the placeholder enters (or is near) the viewport.
 * Eliminates upfront cost of heavy bands on landing (CVE feed, charts, large grids)
 * so initial paint is not blocked by data fetches or large component trees.
 */
export function DeferUntilVisible({
  children,
  rootMargin = "400px",
  minHeight,
  fallback,
}: {
  children: ReactNode;
  rootMargin?: string;
  minHeight?: number | string;
  fallback?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (show) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setShow(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShow(true);
            io.disconnect();
            return;
          }
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [show, rootMargin]);

  return (
    <div ref={ref} style={!show && minHeight ? { minHeight } : undefined}>
      {show ? children : fallback ?? null}
    </div>
  );
}